import React, { useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { companyLogoUrl } from "../../../../apis/company-logo-apis/companyLogoApis";
import { brandOf, fontStack, heroTitleOf } from "../../data/brand";
import { tokens } from "../../styles/publicTheme";
import useBrandFont from "../../hooks/useBrandFont";
import { bannerBackground, toneOf } from "../../components/PublicHero";

// A copy of the top of the public page (nav bar, banner and, when `large`,
// the compliance row) drawn from the form as it is now, so the admin sees a
// change before saving. Uses the public banner's own background and text
// colours. `large`: the size for the editor's preview column.
const BrandPreview = ({ form, logoPath, frameworks = [], large = false }) => {
  const theme = useTheme();
  // Sizes scale together.
  const k = large ? 1.3 : 1;
  const px = (n) => Math.round(n * k * 10) / 10;
  const company = {
    name: form.name,
    overview: form.overview,
    heroTitle: form.heroTitle,
    heroImagePath: form.heroImagePath || null,
    brandColor: form.brandColor,
    secondaryColor: form.secondaryColor,
    bannerStyle: form.bannerStyle,
    bannerStartColor: form.bannerStartColor,
    bannerEndColor: form.bannerEndColor,
    bannerTextColor: form.bannerTextColor,
    fontFamily: form.fontFamily,
    customFontName: form.customFontName,
    // Google Fonts by name: the files are not used.
    customFontFiles:
      form.customFontSource === "google" ? [] : form.customFontFiles || [],
  };
  const brand = brandOf(company);
  // Each preview loads its own copy, so two on screen do not clash.
  const fontId = useRef(
    `trust-center-font-preview-${Math.random().toString(36).slice(2)}`
  );
  useBrandFont(brand.font, fontId.current);
  const t = tokens(brand.accent, brand.secondary, fontStack(brand.font));
  const tone = toneOf(brand.textTone, t);
  const minimal = brand.bannerStyle === "minimal";
  const logo = companyLogoUrl(logoPath);
  const pill = {
    px: 1,
    py: 0.25,
    borderRadius: "999px",
    fontSize: px(10.5),
    border: `1px solid ${tone.line}`,
    backgroundColor: tone.fill,
    color: tone.text,
    whiteSpace: "nowrap",
  };

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mb: 0.75,
          color: theme.customColors.text.secondary,
        }}
      >
        Preview of your public page
      </Typography>
      <Box
        role="img"
        aria-label="Preview of the top of your public page"
        sx={{
          borderRadius: "12px",
          border: `1px solid ${theme.customColors.border.main}`,
          overflow: "hidden",
          backgroundColor: t.paper,
          fontFamily: t.font,
          // Drawn text, not controls.
          userSelect: "none",
          pointerEvents: "none",
        }}
      >
        {/* Nav bar: brand, the open tab underlined, Request access. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 1.5,
            height: px(36),
            backgroundColor: t.surface,
            borderBottom: `1px solid ${t.line}`,
          }}
        >
          <Box
            sx={{
              width: px(18),
              height: px(18),
              borderRadius: "5px",
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              overflow: "hidden",
              backgroundColor: logo ? t.surface : t.accent,
              border: logo ? `1px solid ${t.line}` : "none",
              color: "#fff",
              fontSize: px(10),
              fontWeight: 600,
            }}
          >
            {logo ? (
              <Box
                component="img"
                src={logo}
                alt=""
                sx={{ width: "80%", height: "80%", objectFit: "contain" }}
              />
            ) : (
              (form.name || "?").trim().charAt(0).toUpperCase()
            )}
          </Box>
          <Typography
            noWrap
            sx={{
              fontFamily: "inherit",
              fontSize: px(11.5),
              fontWeight: 600,
              color: t.ink,
              minWidth: 0,
            }}
          >
            {form.name}
          </Typography>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ flex: 1, justifyContent: "center", alignSelf: "stretch" }}
          >
            {["Overview", "Resources", "Controls"].map((label, i) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: px(10.5),
                  color: i === 0 ? t.ink : t.inkSoft,
                  fontWeight: i === 0 ? 600 : 400,
                  borderBottom: `2px solid ${i === 0 ? t.secondary : "transparent"}`,
                }}
              >
                {label}
              </Box>
            ))}
          </Stack>
          <Box
            sx={{
              px: 1,
              py: 0.375,
              borderRadius: "5px",
              backgroundColor: t.accent,
              color: "#fff",
              fontSize: px(10.5),
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            Request access
          </Box>
        </Stack>

        {/* The banner. */}
        <Box sx={{ p: 1.25 }}>
          <Box
            sx={{
              ...bannerBackground(
                t,
                brand,
                companyLogoUrl(company.heroImagePath)
              ),
              borderRadius: "10px",
              px: minimal ? 0.5 : 2,
              py: minimal ? 0.5 : 2,
              color: tone.text,
            }}
          >
            <Typography
              sx={{
                fontFamily: "inherit",
                fontSize: px(17),
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: "-0.02em",
                color: tone.text,
              }}
            >
              {heroTitleOf(company)}
            </Typography>
            {form.overview && (
              <Typography
                sx={{
                  fontFamily: "inherit",
                  mt: 0.75,
                  fontSize: px(11),
                  lineHeight: 1.5,
                  color: tone.soft,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {form.overview}
              </Typography>
            )}
            <Stack direction="row" spacing={0.75} sx={{ mt: 1.25 }}>
              {form.securityEmail && <Box sx={pill}>{form.securityEmail}</Box>}
              <Box sx={pill}>Privacy policy</Box>
            </Stack>
          </Box>
          {large && frameworks.length > 0 && (
            <Box sx={{ mt: 2, px: 0.5 }}>
              <Typography
                sx={{
                  fontFamily: "inherit",
                  fontSize: px(12),
                  fontWeight: 600,
                  color: t.ink,
                  mb: 1,
                }}
              >
                Compliance
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", rowGap: 1 }}
              >
                {frameworks.slice(0, 4).map((f) => (
                  <Box
                    key={f.id}
                    sx={{
                      px: 1.25,
                      py: 0.75,
                      borderRadius: "8px",
                      border: `1px solid ${t.line}`,
                      backgroundColor: t.surface,
                      fontSize: px(11),
                      fontWeight: 500,
                      color: t.inkName,
                    }}
                  >
                    {f.name}
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        fontSize: px(10),
                        fontWeight: 400,
                        color: f.status === "compliant" ? t.verified : t.muted,
                      }}
                    >
                      {f.status === "compliant" ? "Compliant" : "In progress"}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default BrandPreview;
